import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { List, ListItem, ListItemAvatar, ListItemText, Divider, Badge, Box, Stack, Typography, Button, Card, Skeleton, Link } from "@mui/material";
import { Container } from "@mui/system";
import styles from './NotificationList.module.scss';
import NoData from "../../components/noData/noData";
import { HTTPMethod, callApi } from "../../services/HttpService/HttpService";
import { NotificationApi } from "../../constants/ApiConstants";
import { handleTimeFormat } from '../../common/Utils/timeFormat/handleTimeFormat';
import NotificationTitle from './NotificationTitle/NotificationTitle';
import { LineOfBusinessEnum } from '../../constants/BusinessConstants';
import { useDispatch, useSelector } from 'react-redux';
import { fetchNotificationCountData } from '../../common/Utils/NotificationCount/useNotificationCount';
import { checkOrderFeedbackExpired } from '../orders-module/OrdersDetails/OrdersDetails';
import Switch from '@mui/material/Switch';
import { shareFeedbackNotification } from '../../common/enums';
import CustomTablePagination from '../../components/CustomTablePagination/CustomTablePagination';
import Icon from '../../components/Icon/Icon';

const entityTypeIconMap = {
  1: 'fa-clipboard-list',  3: 'fa-clipboard-list',  4: 'fa-clipboard-list',
  9: 'fa-clipboard-list', 10: 'fa-clipboard-list', 12: 'fa-clipboard-list',
  13: 'fa-clipboard-list', 14: 'fa-clipboard-list', 15: 'fa-clipboard-list',
  17: 'fa-clipboard-list', 18: 'fa-clipboard-list', 21: 'fa-clipboard-list',
  22: 'fa-clipboard-list', 23: 'fa-clipboard-list', 28: 'fa-clipboard-list',
  29: 'fa-clipboard-list', 30: 'fa-clipboard-list', 31: 'fa-clipboard-list',
  33: 'fa-clipboard-list', 34: 'fa-clipboard-list', 35: 'fa-clipboard-list',
  36: 'fa-clipboard-list', 37: 'fa-clipboard-list', 38: 'fa-clipboard-list',
  39: 'fa-clipboard-list', 52: 'fa-clipboard-list',
  2: 'fa-user',  5: 'fa-user',  6: 'fa-user', 19: 'fa-user', 20: 'fa-user', 40: 'fa-user',
  7: 'fa-arrows-repeat', 8: 'fa-arrows-repeat', 11: 'fa-arrows-repeat', 16: 'fa-arrows-repeat',
  24: 'fa-clipboard-check', 25: 'fa-clipboard-check', 26: 'fa-clipboard-check', 27: 'fa-clipboard-check',
  32: 'fa-box',
  41: 'fa-file-invoice-dollar', 42: 'fa-file-invoice-dollar', 43: 'fa-file-invoice-dollar',
  44: 'fa-file-invoice-dollar', 45: 'fa-file-invoice-dollar', 46: 'fa-file-invoice-dollar',
  47: 'fa-file-invoice-dollar', 48: 'fa-file-invoice-dollar', 49: 'fa-file-invoice-dollar',
  50: 'fa-file-invoice-dollar', 51: 'fa-file-invoice-dollar',
};

export const Icons = ({ entityType }) => (
  <Icon icon={entityTypeIconMap[entityType]} size="lg" color="primary" />
);

export const handleRedirectTo = (data, e, navigate, navigateToUrl) => {
  const entityType = data.entity_type;
  let url;
  switch (entityType) {
    case 1:
    case 3:
    case 4:
    case 9:
    case 10:
    case 12:
    case 13:
    case 14:
    case 15:
    case 17:
    case 18:
    case 21:
    case 22:
    case 23:
    case 28:
    case 29:
    case 30:
    case 31:
    case 33:
    case 34:
    case 35:
    case 36:
    case 37:
    case 38:
    case 39:
    case 41:
    case 42:
    case 43:
    case 44:
    case 45:
    case 46:
    case 47:
    case 48:
    case 49:
    case 50:
    case 51:
    case 52:
      url = `/orders/order-details/${data.entity_id}`;
      break;
    case 2:
    case 5:
    case 6:
    case 19:
    case 20:
    case 40:
      url = `/${LineOfBusinessEnum.patientsToMembers.toLowerCase()}/${LineOfBusinessEnum.patientToMember.toLowerCase()}-details/${data.entity_id}`;
      break;
    case 7:
    case 8:
    case 11:
    case 16:
      url = `/recurring-orders/recurring-order-details/${data.entity_id}`;
      break;
    case 24:
    case 25:
    case 26:
    case 27:
      url = `/order-approvals/order-approval-details/${data.entity_id}`;
      break;
    case 32:
      url = `/orders/supply-order-details/${data?.entity_id}`;
      break;
    default:
      return;
  }

  const redirectUrl = navigateToUrl || url;
  if (e.ctrlKey) {
    window.open(redirectUrl, '_blank');
  } else {
    navigate(redirectUrl);
  }
};


const NotificationListData = ({showItems, showHeader}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const totalCount = useSelector(state => state.authState.notificationCount?.totalCount);
  const filteredCount = useSelector(state => state.authState.notificationCount?.filteredCount);
  const orderFeedBackExpirationDays = useSelector((state) => state.authState.userData?.data?.application_settings?.order_feed_back_expiration_days)
  const surveyLinkWithAuthForUser = useSelector((state) => state.authState.userData?.data?.application_settings?.survey_link_with_auth_for_user);

  const [notificationListData, setNotificationListData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isShowUnread, setIsShowUnread] = useState(false);

  const [isPaginationLoading, setPaginationLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [nextPagination, setNextPagination] = useState({
    isNextPage: false, 
    paginationIndex: ''
  })
  const [lastPagination, setLastPagination] = useState({
    lastPage: false,
    lastPageCount: 0
  })


  const toggleSetUnRead = (e) => {
    e.stopPropagation();
    setIsShowUnread(e.target.checked);
  };

  const handleReadNotification = async (event, data, navigateToUrl) => {
    event.preventDefault();
    const apiEndpoint = (data?.notification_id) ?
      `${NotificationApi.markAsReadNotification}?NotificationId=${data.notification_id}` :
      `${NotificationApi.markAsReadNotification}?MarkAllAsRead=true`;

    const response = await callApi(HTTPMethod.Get, apiEndpoint);
    if (response && response.status_code == 200 && response.data) {
      fetchNotificationCountData(dispatch)
      if (!data?.notification_id) {
        setNotificationListData(response.data);
        setIsLoading(true);
        await fetchNotificationListData();
      }
      else {
        handleRedirectTo(data, event, navigate, navigateToUrl);
      }
    }
  }

  const handleLastIndexPage = () => {
    if (notificationListData?.older?.length > 0) {
      return notificationListData.older;
    }
    if (notificationListData?.yesterday?.length > 0) {
      return notificationListData.yesterday;
    }
    if (notificationListData?.today?.length > 0) {
      return notificationListData.today;
    } else {
      return [];
    }
  }

  const handlefirstIndexPage = () => {
    if (notificationListData?.today?.length > 0) {
      return notificationListData.today;
    }
    if (notificationListData?.yesterday?.length > 0) {
      return notificationListData.yesterday;
    }
    if (notificationListData?.older?.length > 0) {
      return notificationListData.older;
    } else {
      return [];
    }
  }

  const fetchNotificationListData = async () => {
    setIsLoading(true);

    let apiEndpoint = `${NotificationApi.notificationListing}?OnlyUnReadCount=false&TimeZoneOffset=${new Date().getTimezoneOffset()}`;

    if (isShowUnread) {
      apiEndpoint = apiEndpoint + `&OnlyUnRead=true`;
    }
    if (location.pathname === '/dashboard') {
      apiEndpoint = apiEndpoint + `&Count=3&IsLandingPage=true`;
    } else {
      if (page == 0) {
        apiEndpoint = apiEndpoint + `&Count=${rowsPerPage}&IsNextPage=${true}`;
      } else if (lastPagination.lastPage) {
        apiEndpoint = apiEndpoint + `&Count=${lastPagination.lastPageCount}&IsLastPage=${true}&IsNextPage=${nextPagination.isNextPage}`;
      } else {
        apiEndpoint = apiEndpoint + `&Count=${rowsPerPage}&PaginationIndex=${nextPagination.paginationIndex ? nextPagination.paginationIndex : ''}&IsLastPage=${false}&IsNextPage=${nextPagination.isNextPage}`;
      }
    }

    const response = await callApi(HTTPMethod.Get, apiEndpoint);
    if (response && response.status_code == 200 && response.data) {
      setNotificationListData(response.data);
    }
    setIsLoading(false);
    setPaginationLoading(false);
  };

  useEffect(() => {
    fetchNotificationListData();
  }, [isShowUnread, rowsPerPage, lastPagination.lastPage, page, nextPagination.paginationIndex]);

  const handleChangePage = (_event, newPage, isLastPage = false) => {
    if (page < newPage) {
      const dataArray = handleLastIndexPage();
      setNextPagination({ isNextPage: true, paginationIndex: dataArray[dataArray.length - 1]?.notification_id })
    }
    else {
      const dataArray = handlefirstIndexPage();
      setNextPagination({ isNextPage: false, paginationIndex: dataArray[0]?.notification_id })
    }
    setPage(newPage);
    setLastPagination({ lastPage: false, lastPageCount: 0 })
    if (isLastPage) {
      const count = isShowUnread ? filteredCount : totalCount;
      const lastPageInititalValue = (count - (count % rowsPerPage))
      setLastPagination({ lastPage: true, lastPageCount: count - lastPageInititalValue})
      setNextPagination({ isNextPage: false, paginationIndex: '' })
    }
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPaginationLoading(true);
  }

  const Listing = ({ notificationsList }) => {
    const list = (
      <List disablePadding>
        {notificationsList.map((item, index) => (
          <React.Fragment key={item.notification_id}>
          <ListItem
            alignItems="flex-start"
            data-testid='notificationCard'
            secondaryAction={
              ([shareFeedbackNotification.EntityType31, shareFeedbackNotification.EntityType14].includes(item.entity_type))
              && checkOrderFeedbackExpired(item?.created_on_utc, orderFeedBackExpirationDays)
              && surveyLinkWithAuthForUser
                ? (
                  <Link to='/orders/feedback-order' state={{ orderFeedbackId: item?.entity_id }} onClick={(e) => e.stopPropagation()}>
                    {t("SupportComponent.share_feedback")}
                  </Link>
                ) : undefined
            }
          >
            <ListItemAvatar data-testid='gridNotification'>
              {item?.is_read
                ? <Icons entityType={item.entity_type} />
                : (
                  <Badge color="error" overlap="circular" badgeContent=" " variant="dot" anchorOrigin={{ vertical: 'top', horizontal: 'left' }}>
                    <Icons entityType={item.entity_type} />
                  </Badge>
                )
              }
            </ListItemAvatar>
            <ListItemText
              id={item.notification_id}
              primary={<Typography variant="h6"><NotificationTitle title={item?.title} data={item} readNotification={handleReadNotification} /></Typography>}
              secondary={
                <>
                  <Typography color="text.secondary">{handleTimeFormat(item?.created_on_utc)}</Typography>
                  <Typography><NotificationTitle title={item?.body} data={item} readNotification={handleReadNotification} /></Typography>
                </>
              }
            />
          </ListItem>
          {index < notificationsList.length - 1 && <Divider variant="inset" component="li" />}
        </React.Fragment>
      ))}
      </List>
    );
    return location.pathname === '/dashboard' ? list : <Card>{list}</Card>;
  }

  return (
    <Box data-testid="NotificationList">
      {showHeader ?
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant='h3'>{t("Dashboard.notifications")}</Typography>
            <Button 
              variant='tertiary'
              onClick={(e) => { handleReadNotification(e) }}
              data-testid='markAllRead'
            >
              {t("Dashboard.markAllAsRead")}
            </Button>
          </Stack>
          <Stack direction="row" alignItems="center" mt={2} gap={2}>
            <Switch
              data-testid="antSwitch"
              checked={isShowUnread}
              onChange={toggleSetUnRead}
              inputProps={{ "aria-label": "only show unread" }}
            />
            <Typography className={styles.toggleBtnText}>
              {t("Dashboard.onlyShowUnread")}
            </Typography>
          </Stack>
          {(() => {
            const sections = [
              { key: 'today',     label: t("Dashboard.today") },
              { key: 'yesterday', label: t("Dashboard.yesterday") },
              { key: 'older',     label: t("Dashboard.older") },
            ];
            const hasData = sections.some(({ key }) => notificationListData?.[key]?.length > 0);
            return hasData ? (
              <>
                {sections.map(({ key, label }) =>
                  notificationListData?.[key]?.length > 0 && (
                    <Stack key={key} mt={6} gap={2}>
                      <Typography variant='h5'>{label}</Typography>
                      <Listing notificationsList={notificationListData[key]} />
                    </Stack>
                  )
                )}
                <CustomTablePagination
                  totalRecord={isShowUnread ? filteredCount : totalCount}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  isLoading={isPaginationLoading}
                />
              </>
            ) : isLoading ? (
              <Stack spacing={1} mt={3}>
                {[...Array(5)].map((_, i) => (
                  <Stack key={i} direction="row" spacing={2} alignItems="flex-start" sx={{ py: 1.5, ...(i < 4 && { borderBottom: '1px solid', borderColor: 'divider' }) }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
                    <Stack spacing={0.75} flex={1}>
                      <Skeleton variant="text" width="50%" height={22} />
                      <Skeleton variant="text" width="25%" />
                      <Skeleton variant="text" width="80%" />
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Stack mt={3}><NoData message={t('Dashboard.noNotificationExits')} /></Stack>
            );
          })()}
        </>
        :
        <>
          {notificationListData?.is_landing_page?.length > 0 ?
            <Stack className={styles.landingPageBox}><Listing notificationsList={notificationListData?.is_landing_page} /></Stack> :
            isLoading ? (
              <Stack spacing={1}>
                {[...Array(3)].map((_, i) => (
                  <Stack key={i} direction="row" spacing={2} alignItems="flex-start" sx={{ py: 1.5, ...(i < 2 && { borderBottom: '1px solid', borderColor: 'divider' }) }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
                    <Stack spacing={0.75} flex={1}>
                      <Skeleton variant="text" width="50%" height={22} />
                      <Skeleton variant="text" width="25%" />
                      <Skeleton variant="text" width="80%" />
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <NoData message={t('Dashboard.noRecentNotification')} />
            )
          }
        </>
      }
    </Box>
  )
};

const NotificationList = ({ showItems, showHeader = true }) => {
  return (
    location.pathname === '/dashboard' ?
      <NotificationListData showItems={showItems} showHeader={showHeader} /> :
      <Container className="mainContainer">
          <NotificationListData showItems={showItems} showHeader={showHeader} />
      </Container>
  )
}

export default NotificationList;
